import { reactive, computed } from "vue";
import { useI18n } from "vue-i18n";
import {
  createNotary,
  deleteNotary,
  getNotaryItem,
  updateNotary,
} from "@/entities/session/api/notaries";
import { getUserItem } from "@/entities/user";
import { getOrganizationList } from "@/entities/organization";
import { getRoleList } from "@/entities/role";
import { toast } from "@/shared/store/alertStore";
import {
  PROFILE_FIELDS,
  USER_FIELDS,
  clone,
  pickFields,
  emptyData,
} from "@/widgets/profile-drawer/model/notaryFields";

/**
 * Composable for the notary drawer.
 *
 * uuid-driven, no "own/self" branch. The drawer is opened for a selected
 * notary by uuid. To see your own notary profile, pass your own uuid.
 *
 * Modes:
 *   - "create" — pick a user via UserPicker, then POST /notaries/.
 *   - "edit"   — GET /notaries/{uuid}/ → PUT /notaries/{uuid} (profile only).
 *   - "view"   — GET /notaries/{uuid}/ (read-only).
 *
 * Through this drawer USER fields (name, IIN, email, phone, organization,
 * role) are never editable — they belong to the user entity. Edits go
 * through the user drawer or the user admin page.
 */
export function useNotaryDrawerData() {
  const { t } = useI18n();
  const state = reactive({
    loading: false,
    saving: false,
    deleting: false,
    error: null,
    data: {},
    original: {},
    mode: "view",
    notaryUuid: null,
    editMode: false,
    selectedUser: null, // populated in create mode after UserPicker
    organizations: [],
    roles: [],
  });

  const dirty = computed(
    () => JSON.stringify(state.data) !== JSON.stringify(state.original)
  );

  let dictionariesLoaded = false;

  async function ensureDictionaries() {
    if (dictionariesLoaded) return;
    try {
      const [orgsResp, rolesResp] = await Promise.all([
        getOrganizationList({}),
        getRoleList({}),
      ]);
      if (orgsResp?.success) state.organizations = orgsResp.payload || [];
      if (rolesResp?.success) state.roles = rolesResp.payload?.items || [];
      dictionariesLoaded = true;
    } catch (e) {
      console.error(e);
    }
  }

  async function load(uuid, mode = "view") {
    state.loading = true;
    state.error = null;
    state.mode = mode;
    state.notaryUuid = uuid || null;
    state.editMode = mode === "edit" || mode === "create";
    state.selectedUser = null;
    state.data = {};
    state.original = {};

    await ensureDictionaries();

    if (mode === "create") {
      const seed = emptyData();
      state.data = clone(seed);
      state.original = clone(seed);
      state.loading = false;
      return;
    }

    if (!uuid) {
      state.error = new Error("No notary uuid");
      state.loading = false;
      return;
    }

    try {
      const res = await getNotaryItem(uuid);
      const payload = res?.payload ?? res ?? {};
      const merged = { ...emptyData(), ...payload };

      // The /notaries/{uuid} response may carry the organization either as
      // a flat `organization_id` field, an `organization` object, or nested
      // under `user`. Pick the first non-null id so the dictionary lookup
      // in the template can find a name.
      if (merged.organization_id == null) {
        merged.organization_id =
          payload?.organization?.id ??
          payload?.user?.organization_id ??
          payload?.user?.organization?.id ??
          payload?.profile?.organization_id ??
          null;
      }

      state.data = clone(merged);
      state.original = clone(merged);
    } catch (e) {
      console.error(e);
      state.error = e;
    } finally {
      state.loading = false;
    }
  }

  /**
   * Called in create mode when the user picks an existing user.
   * Fetches the full user record and seeds USER_FIELDS into state.data.
   */
  async function selectUser(userId) {
    if (!userId) {
      state.selectedUser = null;
      const empty = emptyData();
      for (const k of USER_FIELDS) state.data[k] = empty[k];
      return;
    }
    try {
      const resp = await getUserItem(userId);
      const user = resp?.payload || {};
      state.selectedUser = {
        uuid: user.uuid || user.id || userId,
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        middle_name: user.middle_name || "",
        identifier: user.identifier || "",
        email: user.email || "",
        phone: user.phone || "",
      };
      for (const k of USER_FIELDS) {
        if (user[k] !== undefined) state.data[k] = user[k];
      }
      if (state.data.organization_id == null && user?.organization?.id) {
        state.data.organization_id = user.organization.id;
      }
    } catch (e) {
      console.error(e);
      toast.warning(t("Не удалось загрузить пользователя"));
    }
  }

  function update(key, value) {
    state.data[key] = value;
  }

  function updateArrayItem(arrKey, idx, key, value) {
    const arr = Array.isArray(state.data[arrKey])
      ? [...state.data[arrKey]]
      : [];
    arr[idx] = { ...(arr[idx] || {}), [key]: value };
    state.data[arrKey] = arr;
  }

  function addArrayItem(arrKey, template) {
    const arr = Array.isArray(state.data[arrKey])
      ? [...state.data[arrKey]]
      : [];
    arr.push(clone(template));
    state.data[arrKey] = arr;
  }

  function removeArrayItem(arrKey, idx) {
    const arr = Array.isArray(state.data[arrKey])
      ? [...state.data[arrKey]]
      : [];
    arr.splice(idx, 1);
    state.data[arrKey] = arr;
  }

  function toggleService(code, on) {
    const codes = Array.isArray(state.data.notarial_actions_codes)
      ? [...state.data.notarial_actions_codes]
      : [];
    const idx = codes.indexOf(code);
    if (on && idx === -1) codes.push(code);
    if (!on && idx !== -1) codes.splice(idx, 1);
    state.data.notarial_actions_codes = codes;
  }

  function cancel() {
    state.data = clone(state.original);
    state.editMode = state.mode === "create";
  }

  function startEdit() {
    state.editMode = true;
    if (state.mode === "view") state.mode = "edit";
  }

  function validate() {
    if (state.mode === "create" && !state.selectedUser) {
      toast.warning(t("Выберите пользователя"));
      return false;
    }
    return true;
  }

  async function save() {
    if (state.saving) return null;
    if (!validate()) return { validationFailed: true };
    state.saving = true;
    try {
      if (state.mode === "create") {
        const user = pickFields(state.data, USER_FIELDS);
        const profile = pickFields(state.data, PROFILE_FIELDS);
        return await createNotary({ user, profile });
      }
      if (state.mode === "edit" && state.notaryUuid) {
        const profilePayload = pickFields(state.data, PROFILE_FIELDS);
        const resp = await updateNotary(state.notaryUuid, profilePayload);
        state.original = clone(state.data);
        state.editMode = false;
        state.mode = "view";
        return resp;
      }
      return null;
    } finally {
      state.saving = false;
    }
  }

  async function remove() {
    if (!state.notaryUuid || state.deleting) return null;
    state.deleting = true;
    try {
      return await deleteNotary(state.notaryUuid);
    } finally {
      state.deleting = false;
    }
  }

  return {
    state,
    dirty,
    load,
    selectUser,
    update,
    updateArrayItem,
    addArrayItem,
    removeArrayItem,
    toggleService,
    cancel,
    startEdit,
    save,
    remove,
  };
}
